/**
 * RFC-0392: WebSocket connection for real-time chat events.
 *
 * Replaces the SSE streaming in useAgentChat. Maintains a persistent
 * WebSocket connection to /ws/chat/{projectId} with automatic reconnection
 * and exponential backoff.
 *
 * Terminal error code 4001 (missing auth) suppresses reconnection.
 * 4003 (expired token) allows reconnection — getAuthToken() fetches a fresh token.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { getConfiguredApiBase, getAuthToken } from '../api/client'
import type { DirectorTurnPresentation } from '../generated/directorChatTypes'
import { createDebug, createNadDebug } from '../utils/debugLog'
const log = createDebug('dw:chat:socket')
const wsEventLog = createNadDebug('dw:chat:ws-event')

export interface ChatEventQuestionOption {
  value?: string
  label?: string
  description?: string
}

export interface ChatEventExecutionPlanNode {
  node_id?: string
  type?: string
  action?: string | null
  label?: string
  sparks?: number
}

export interface ChatEventLibraryPickerItem {
  node_id: string
  node_type?: string | null
  kind?: string | null
  label?: string | null
  descriptor?: string | null
  status?: string | null
  out_image?: string | null
  out_video?: string | null
  out_audio?: string | null
}

export interface ChatEventCreateProjectMeta {
  name: string
  context: string
  library_node_ids?: string[]
  style?: string | null
  production_target?: string | null
  aspect_ratio?: string | null
}

export interface ChatEventByomCapabilities {
  duration_range: string[]
  aspect_ratios: string[]
  resolutions: string[]
  audio_control: string[]
  reference_images_support: string[]
  reference_videos_support: string[]
  reference_audios_support: string[]
  max_total_references: string[]
}

export interface ChatEventByomConfiguration {
  action: 'create' | 'update'
  key_name: string
  provider_model?: string | null
  capabilities: ChatEventByomCapabilities
}

export interface ChatEventVoiceCatalogueSelectionContext {
  type: 'character' | 'sound_expert' | 'script_voice_role' | 'video_editor_change_voice'
  node_id: string
  target_id?: string | null
  voice_role_id?: string | null
  target_label?: string | null
  selected_voice_ref?: string | null
}

/** Identity descriptor for the specific clip/panel a decision card is about.
 *  Backend-resolved (deterministic); the frontend only formats/localizes it into
 *  a small "Clip N · vN" / "Panel N · vN" chip. Absent → no chip (fail-closed).
 *  `node_id` is metadata-only and never rendered (reserved for a future
 *  "show on canvas" affordance). */
export interface ChatEventQuestionNodeRef {
  node_id: string
  kind: 'clip' | 'panel' | 'generic'
  number?: number | null
  version?: number | null
  name?: string | null
}

export interface ChatEventQuestionMetadata {
  nodes?: ChatEventExecutionPlanNode[]
  total_sparks?: number
  /** Set when the question is about one specific clip/panel node → renders an
   *  identity chip in the QuestionCard header. */
  node_ref?: ChatEventQuestionNodeRef
  /** When 'library_picker', the question renders the visual ReferenceSelector
   *  picker (seeded from `items`) instead of a plain text QuestionCard.
   *  When 'create_project_button', renders a CreateProjectCard. */
  render_as?: string
  director_id?: string
  target_canvas_id?: string
  round_number?: number
  kind?: string | null
  items?: ChatEventLibraryPickerItem[]
  /** Sequel / new-project proposal metadata (render_as='create_project_button'). */
  create_project?: ChatEventCreateProjectMeta
  /** Exact credential-free specialist proposal shown before BYOM confirmation. */
  byom_configuration?: ChatEventByomConfiguration
  /** render_as='global_config_action_card': why the Director needs a change. */
  reason?: string
  /** render_as='global_config_action_card': Global Config fields to show live. */
  settings_hint?: string[]
  /** render_as='voice_catalogue_action_card': graph target for the selected voice. */
  selection_context?: ChatEventVoiceCatalogueSelectionContext
}

export interface ChatEventQuestion {
  question_type?: string
  tool_use_id?: string
  question?: string
  options?: ChatEventQuestionOption[]
  allow_custom_input?: boolean
  header?: string
  answer?: string | null
  selected_option?: string | null
  custom_text?: string | null
  metadata?: ChatEventQuestionMetadata
}

export interface ChatEventSelectedSkill {
  ref: string
  version_id: string
  name: string
  public_slug: string
  display_name: string
  position: number
}

export interface ChatEventActivity {
  state: 'in_progress'
}

export interface ChatEventMessage {
  metadata?: object
  presentation?: DirectorTurnPresentation
  id?: string
  role?: string
  agent?: string
  content?: string
  referenced_node_ids?: string[]
  selected_skill?: ChatEventSelectedSkill
  timestamp?: string
  questions?: ChatEventQuestion[]
  activity?: ChatEventActivity
  isStreaming?: boolean
  wasStreamed?: boolean
  isSystemNotification?: boolean
}

export interface ChatEventCanvasSummary {
  id: string
  agent_type?: 'director' | 'dreamvibe'
  agent_id?: string
  director_id: string
  title: string
  updated_at?: string
  created_at?: string
  node_count?: number
}

export interface ChatEvent extends ChatEventMessage {
  type: string
  status?: string
  state?: string
  messages?: ChatEventMessage[]
  canvas?: ChatEventCanvasSummary
  error?: string | null
  message?: string
  message_id?: string
  has_tool_use?: boolean
  tool_name?: string
  tool_use_id?: string
  node_id?: string
  action?: string | null
  code?: number
  batch_id?: string
  batch_revision?: number
  success?: boolean
  total_count?: number
  finished_count?: number
  ready_count?: number
  failed_count?: number
  applied_count?: number
  skipped_count?: number
  /** BYOM models that failed to load — shown as toast warnings once. */
  byom_warnings?: string[]
  /** `project_config_changed`: committed Global Config fields + who wrote them. */
  changed_fields?: string[]
  actor?: string | null
  /** `project_config_changed`: cost classes the Director turned auto-confirm off for. */
  auto_confirm_off?: string[]
  canvas_id?: string
}

function isChatEvent(value: Partial<ChatEvent> | null): value is ChatEvent {
  return value !== null && typeof value.type === 'string'
}

interface UseChatSocketOptions {
  projectId: string
  canvasId?: string
  enabled?: boolean
  onEvent: (event: ChatEvent) => void
}

/** First drop of the current outage; diagnostics only — never shown. */
interface ChatSocketDrop {
  kind: 'connection_lost' | 'heartbeat_timeout' | 'send_failed'
  code?: number
  reason?: string
  occurredAt: number
  reconnectAttempt: number
}

/**
 * - `connecting`: first connect, nothing received yet.
 * - `connected`: the socket delivered at least one message since it opened.
 * - `reconnecting`: the socket dropped and has not yet delivered a message on
 *   a new connection. Never an error, however long it lasts — the Director
 *   keeps running server-side, reconnects retry with backoff, and the
 *   reconnect snapshot reconciles the chat.
 * - `disconnected`: the server closed with a terminal auth code (4001); no
 *   reconnect is attempted.
 */
export type ChatSocketConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected'

interface UseChatSocketResult {
  connected: boolean
  connectionState: ChatSocketConnectionState
}

function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

const MIN_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000

/** Close codes that indicate a terminal auth error — retrying won't help.
 *  4003 (expired token) is NOT terminal: reconnect fetches a fresh token via getAuthToken(). */
const TERMINAL_CLOSE_CODES = new Set([4001])

/** Liveness watchdog: send a ping every Nms; force reconnect if no inbound
 *  message arrives for Mms. Catches silent disconnects (server killed but
 *  TCP/LB don't surface the close). 2s/5s = ~5s detection. */
const HEARTBEAT_INTERVAL_MS = 2000
const HEARTBEAT_DEAD_MS = 5000

export function useChatSocket({
  projectId,
  canvasId = 'agent',
  enabled = true,
  onEvent,
}: UseChatSocketOptions): UseChatSocketResult {
  const [connected, setConnected] = useState(false)
  const [connectionState, setConnectionState] = useState<ChatSocketConnectionState>('connecting')
  /** The first drop of the current outage, held until a reconnect delivers. */
  const pendingDropRef = useRef<ChatSocketDrop | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(MIN_RECONNECT_DELAY)
  const onEventRef = useRef(onEvent)
  const mountedRef = useRef(true)
  /** Set to true when a terminal error event is received — suppresses reconnect. */
  const terminalErrorRef = useRef(false)
  /** Tracks how many times this connection has reconnected (for diagnostics). */
  const reconnectCountRef = useRef(0)
  const activeScopeRef = useRef({ projectId, canvasId })

  onEventRef.current = onEvent
  activeScopeRef.current = { projectId, canvasId }

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setConnected(false)
  }, [])

  /** A drop starts (or continues) an outage. Only the first drop is kept —
   *  the heartbeat path closes the socket, so one outage usually reports
   *  `heartbeat_timeout` then `connection_lost`. */
  const reportDrop = useCallback((drop: Omit<ChatSocketDrop, 'occurredAt' | 'reconnectAttempt'>) => {
    if (pendingDropRef.current) return
    const pendingDrop: ChatSocketDrop = {
      ...drop,
      occurredAt: Date.now(),
      reconnectAttempt: reconnectCountRef.current,
    }
    pendingDropRef.current = pendingDrop
    wsEventLog({
      event: 'connection-drop',
      kind: pendingDrop.kind,
      code: pendingDrop.code ?? null,
      reason: pendingDrop.reason ?? '',
      reconnectAttempt: pendingDrop.reconnectAttempt,
    })
    setConnectionState('reconnecting')
  }, [])

  /** The current socket delivered a message: the outage (if any) is over. */
  const markHealthy = useCallback(() => {
    const outage = pendingDropRef.current
    pendingDropRef.current = null
    if (outage) {
      wsEventLog({
        event: 'connection-recovered',
        kind: outage.kind,
        downMs: Date.now() - outage.occurredAt,
        reconnectAttempt: reconnectCountRef.current,
      })
    }
    setConnectionState('connected')
  }, [])

  useEffect(() => {
    mountedRef.current = true
    terminalErrorRef.current = false
    // New scope (or first mount): a prior project's outage must not leak in.
    pendingDropRef.current = null
    setConnectionState('connecting')
    if (!enabled || !projectId) {
      cleanup()
      return
    }

    let cancelled = false
    // Heartbeat state: lastMessageAt resets on any inbound message;
    // heartbeatTimer drives both ping send + dead-timeout check.
    let lastMessageAt = Date.now()
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null
    const socketProjectId = projectId
    const socketCanvasId = canvasId

    const isCurrentConnection = (ws: WebSocket) =>
      !cancelled &&
      wsRef.current === ws &&
      activeScopeRef.current.projectId === socketProjectId &&
      activeScopeRef.current.canvasId === socketCanvasId

    const stopHeartbeat = () => {
      if (heartbeatTimer != null) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
      }
    }

    const startHeartbeat = (ws: WebSocket) => {
      stopHeartbeat()
      heartbeatTimer = setInterval(() => {
        if (!isCurrentConnection(ws)) return
        // Background tabs throttle timers heavily. A throttled interval can wake
        // after HEARTBEAT_DEAD_MS even when the socket is healthy, causing a
        // reconnect loop and noisy NAD logs while the workspace is not focused.
        if (document.visibilityState !== 'visible') {
          lastMessageAt = Date.now()
          return
        }
        if (Date.now() - lastMessageAt > HEARTBEAT_DEAD_MS) {
          wsEventLog('heartbeat-timeout, forcing reconnect')
          reportDrop({
            kind: 'heartbeat_timeout',
          })
          stopHeartbeat()
          // ws.close() triggers onclose → scheduleReconnect()
          try { ws.close() } catch { /* already closed */ }
          return
        }
        if (ws.readyState !== WebSocket.OPEN) return
        try {
          ws.send('ping')
        } catch {
          // send failed — connection probably dead
          reportDrop({
            kind: 'send_failed',
          })
          stopHeartbeat()
          try { ws.close() } catch { /* already closed */ }
        }
      }, HEARTBEAT_INTERVAL_MS)
    }

    // When tab returns to foreground, reset lastMessageAt so a long
    // background-throttled gap doesn't immediately trigger a false-positive
    // reconnect. The next ping/pong cycle will surface a real disconnect
    // within HEARTBEAT_DEAD_MS.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastMessageAt = Date.now()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const connect = async () => {
      if (cancelled) return

      const apiBase = getConfiguredApiBase()
      const wsBase = toWsUrl(apiBase)
      const token = await getAuthToken()
      if (cancelled) return

      const params = new URLSearchParams()
      if (token) {
        params.set('token', token)
      }
      const queryString = params.toString()
      const url = `${wsBase}/ws/chat/${socketProjectId}/${socketCanvasId}${queryString ? `?${queryString}` : ''}`

      const ws = new WebSocket(url)
      wsRef.current = ws
      // `onopen` alone is not proof of health: the server only reads pings
      // after it has sent the initial `state` snapshot, so a slow setup can
      // open a socket that then heartbeat-times out. The first inbound message
      // is what ends an outage.
      let deliveredMessage = false

      ws.onopen = () => {
        if (!isCurrentConnection(ws)) return
        log('connected projectId=%s canvasId=%s', socketProjectId, socketCanvasId)
        wsEventLog('connect projectId=%s canvasId=%s reconnectCount=%d', socketProjectId, socketCanvasId, reconnectCountRef.current)
        setConnected(true)
        reconnectDelayRef.current = MIN_RECONNECT_DELAY
        lastMessageAt = Date.now()
        startHeartbeat(ws)
      }

      ws.onmessage = (event) => {
        if (!isCurrentConnection(ws)) return
        // Reset watchdog on any inbound message — including the pong, keepalive,
        // or any real event. This is what makes the silent-disconnect case
        // detectable.
        lastMessageAt = Date.now()
        if (!deliveredMessage) {
          deliveredMessage = true
          markHealthy()
        }
        try {
          const parsed: Partial<ChatEvent> | null = JSON.parse(event.data)
          if (!isChatEvent(parsed)) return
          const data = parsed
          if (data.type === 'keepalive' || data.type === 'pong') return

          wsEventLog('message type=%s', data.type)

          // Handle server-sent error events (accept-first error handling).
          // Terminal auth errors (4001) suppress reconnection.
          if (data.type === 'error' && typeof data.code === 'number') {
            if (TERMINAL_CLOSE_CODES.has(data.code)) {
              terminalErrorRef.current = true
              wsEventLog('terminal-error code=%d', data.code)
            }
          }

          onEventRef.current(data)
        } catch {
          // ignore non-JSON messages (e.g. "pong")
        }
      }

      ws.onclose = (event) => {
        if (!isCurrentConnection(ws)) return
        log('disconnected code=%d reason=%s', event.code, event.reason)
        wsEventLog('disconnect code=%d reason=%s wasTerminal=%s', event.code, event.reason, terminalErrorRef.current || TERMINAL_CLOSE_CODES.has(event.code))
        setConnected(false)
        stopHeartbeat()

        // Don't reconnect on terminal auth errors — they won't resolve.
        if (terminalErrorRef.current || TERMINAL_CLOSE_CODES.has(event.code)) {
          pendingDropRef.current = null
          setConnectionState('disconnected')
          return
        }

        reportDrop({
          kind: 'connection_lost',
          code: event.code,
          reason: event.reason,
        })
        scheduleReconnect()
      }

      ws.onerror = () => {
        // onclose will fire after onerror, which handles reconnection
      }
    }

    const scheduleReconnect = async () => {
      if (cancelled || !mountedRef.current) return
      const delay = reconnectDelayRef.current
      reconnectCountRef.current++
      log('reconnect in %dms', delay)
      wsEventLog('reconnect-scheduled delayMs=%d attempt=%d', delay, reconnectCountRef.current)
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY)
      reconnectTimerRef.current = setTimeout(() => {
        if (!cancelled && mountedRef.current) {
          connect()
        }
      }, delay)
    }

    connect()

    return () => {
      cancelled = true
      mountedRef.current = false
      stopHeartbeat()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      cleanup()
    }
  }, [projectId, canvasId, enabled, cleanup, reportDrop, markHealthy])

  return { connected, connectionState }
}
